import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import { featureFlags } from '../../config/featureFlags'
import {
  fetchUnifiedExecutiveContext,
  type AssetInventoryExecutiveAsset,
} from '../../services/enterpriseOperationalUxApi'
import type { UnifiedExecutiveContext } from '../../types/unifiedExecutiveContext'
import ExecutiveOperationalContextPanel from './context/ExecutiveOperationalContextPanel'
import OperationalRuntimeTimeline from './context/OperationalRuntimeTimeline'
import OperationalBlastRadiusPanel from './context/OperationalBlastRadiusPanel'
import { pressureBadgeClass, pressureColor } from '../../utils/operationalUx'
import { formatScanTimestamp } from '../../utils/scanTime'
import { getStandardDisplayName } from '../../utils/complianceStandard'
import styles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

type Props = {
  assetId: string
  assetName?: string
  legacySlot?: ReactNode
}

export default function AssetDetailsExecutiveWorkspace({ assetId, assetName, legacySlot }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [context, setContext] = useState<UnifiedExecutiveContext | null>(null)
  const [entity, setEntity] = useState<AssetInventoryExecutiveAsset | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchUnifiedExecutiveContext('asset', assetId, assetName)
      if (data?.context) {
        setContext(data.context)
        setEntity(data.entity as AssetInventoryExecutiveAsset | null)
      } else {
        setContext(null)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load operational context')
    } finally {
      setLoading(false)
    }
  }, [assetId, assetName])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) {
    return <p className={styles.emptyState}>{t('common.loading', 'Loading…')}</p>
  }

  if (error) {
    return <p className={styles.emptyState}>{error}</p>
  }

  const band = context?.pressure?.band
  const exposure = entity?.exposure

  return (
    <div className={styles.assetDetailsWorkspace} data-testid="asset-details-executive-workspace">
      <section className={styles.assetDetailsSection}>
        <h2 className={styles.assetDetailsSectionTitle}>
          {t('enterpriseOperationalUx.assetDetails.header', 'Executive asset operational workspace')}
        </h2>
        <div className={styles.contextHeader}>
          <div>
            <h3 style={{ margin: 0, color: '#f8fafc' }}>{context?.title || assetName || assetId}</h3>
            <p className={styles.scopeLine}>
              {entity?.type} · {entity?.environment || '—'} · {entity?.criticality || '—'}
              {entity?.ownerLabel ? ` · ${entity.ownerLabel}` : ''}
            </p>
            {entity?.networkTarget ? (
              <p className={styles.scopeLine}>{entity.networkTarget}</p>
            ) : null}
            <p className={styles.scopeLine}>
              {t('scan.last_scan', 'Last scan')}: {formatScanTimestamp(entity?.lastScan)}
            </p>
          </div>
          {band ? (
            <span className={pressureBadgeClass(band, styles)} style={{ color: pressureColor(band as 'LOW') }}>
              {band}
            </span>
          ) : null}
        </div>
        {exposure?.internetFacing ? (
          <p className={styles.postureStory}>
            {t('enterpriseOperationalUx.context.sharedOperationalImpact', 'Shared operational impact')}:{' '}
            {t('enterpriseOperationalUx.assetInventory.internetFacing', 'Internet-facing')}
          </p>
        ) : null}
      </section>

      <div className={styles.assetDetailsGrid}>
        <section className={styles.assetDetailsSection}>
          <h3 className={styles.assetDetailsSectionTitle}>
            {t('enterpriseOperationalUx.assetDetails.assurance', 'Assurance')}
          </h3>
          <dl className={styles.metricList}>
            <div className={styles.metricRow}>
              <dt>{t('enterpriseOperationalUx.assetInventory.continuousAssurance', 'Continuous assurance')}</dt>
              <dd>{entity?.assurance.assuranceLevel || '—'}</dd>
            </div>
            <div className={styles.metricRow}>
              <dt>{t('enterpriseOperationalUx.assetDetails.telemetryCoverage', 'Telemetry coverage')}</dt>
              <dd>{entity?.assurance.telemetryPresent ? t('common.yes', 'Yes') : t('common.no', 'No')}</dd>
            </div>
            <div className={styles.metricRow}>
              <dt>{t('enterpriseOperationalUx.assetDetails.operationalState', 'Operational state')}</dt>
              <dd>{entity?.assurance.controlHealth || '—'}</dd>
            </div>
          </dl>
        </section>

        <section className={styles.assetDetailsSection}>
          <h3 className={styles.assetDetailsSectionTitle}>
            {t('enterpriseOperationalUx.assetDetails.complianceImpact', 'Compliance impact')}
          </h3>
          <p className={styles.scopeLine}>
            {entity?.complianceApplicability.standardsAffected?.length
              ? entity.complianceApplicability.standardsAffected
                  .map((c) => getStandardDisplayName(t, c))
                  .join(', ')
              : '—'}
          </p>
          <p className={styles.scopeLine}>
            {t('enterpriseOperationalUx.assetDetails.linkedSystems', 'Linked systems')}: {entity?.linkedSystemsCount ?? 0}
          </p>
        </section>

        <section className={styles.assetDetailsSection}>
          <h3 className={styles.assetDetailsSectionTitle}>
            {t('enterpriseOperationalUx.assetDetails.runtime', 'Runtime')}
          </h3>
          <OperationalRuntimeTimeline events={context?.runtimeEvents || []} mode="full" maxItems={10} />
          <OperationalBlastRadiusPanel hits={context?.blastRadius.hits || []} mode="expanded" />
        </section>

        <section className={styles.assetDetailsSection}>
          <h3 className={styles.assetDetailsSectionTitle}>
            {t('enterpriseOperationalUx.assetDetails.vulnerabilities', 'Vulnerabilities')}
          </h3>
          {entity?.vulnerability.topVulnerabilities?.length ? (
            <ul className={styles.recommendationList}>
              {entity.vulnerability.topVulnerabilities.map((v) => (
                <li key={v.id}>
                  <strong>{v.title}</strong>
                  {v.lifecycleState ? ` · ${v.lifecycleState}` : ''}
                  {v.severity ? ` · ${v.severity}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.emptyState}>
              {t('enterpriseOperationalUx.context.noOperationalDegradation', 'No operational degradation')}
            </p>
          )}
        </section>
      </div>

      <section className={styles.assetDetailsSection}>
        <ExecutiveOperationalContextPanel context={context} mode="full" dark />
      </section>

      {legacySlot ? <div className={styles.legacySection}>{legacySlot}</div> : null}

      <div className={styles.cardActions}>
        {featureFlags.COMPLIANCE_RUNTIME_V1 ? (
          <Button type="button" size="small" color="outline" onClick={() => navigate('/compliance-center/operational-events')}>
            {t('enterpriseOperationalUx.openRuntimeEvents', 'Runtime events')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
